// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — transactional audit staging + ambient bridge-turn marker
//                        (ABB-S5a, INV-AUDIT-TX / INV-SOURCE)
// ═══════════════════════════════════════════════════════════════════════════
//
// Two cooperating responsibilities, both PURE + LEAF (this module imports
// NOTHING — mirrors `inFlightLatch.ts`, ПРИОРИТЕТ #2):
//
//   (1) AMBIENT BRIDGE-TURN MARKER — a process-wide flag set around the
//       SYNCHRONOUS safe-contour call (S4 wires `beginBridgeTurn` /
//       `endBridgeTurn` as its `beginBridgeTurn?`/`endBridgeTurn?` DI, calling
//       them in a `try/finally` around `runContour`). While the marker is active,
//       `state/reducerHelpers.ts` reads it (a STATE→agentBridge edge, the SAME
//       allowed one-directional edge `editorStore → agentBridge/inFlightLatch`
//       already uses — this module imports no state, so there is NO cycle; verified
//       by `npm run deps:graph` cycles=0) and OVERRIDES the audit-ring entry's
//       `actorEmail` to the synthetic bridge actor + STAMPS `channel:'agent-bridge'`
//       (INV-SOURCE: `source` stays `'system'` — the channel is the distinct typed
//       attribution, NOT a widening of the closed `commandSourceSchema`). The
//       marker is ALSO the distinct execution-principal slot (ABB-S4 P3): any
//       lock/authority check during the turn sees the non-user bridge principal so
//       a COLLAB-locked entity still rejects.
//
//       CRITICAL (the load-bearing contamination invariant): `endBridgeTurn` MUST
//       run in S4's `finally`, so the marker CLEARS even on a thrown / aborted /
//       superseded turn. A leaked marker would make the NEXT user turn's audit ring
//       entries falsely attribute to the bridge actor/channel. The marker is a
//       NON-reentrant depth counter (defence-in-depth: nested begins are balanced
//       by their ends; the single-in-flight latch already forbids true nesting).
//
//   (2) STAGED-AUDIT PROJECTION — because the contour is SYNCHRONOUS with no
//       post-commit hook, S5 reads the audit high-water (`state.audit.nextSequence`)
//       IMMEDIATELY BEFORE + AFTER the contour call. On SUCCESS the events whose
//       `sequence` falls in the HALF-OPEN range `[preHighWater, postHighWater)` are
//       EXACTLY this turn's events → projected as the `audit` push payload. On a
//       thrown (aborted) turn the range is DISCARDED (NOT pushed) — `history/
//       abort-batch` dispatches INVERSE commands that THEMSELVES append audit
//       entries (the abort does NOT, by itself, revert the audit ring). Two
//       cooperating facts make the discard SAFE:
//         (i)  the caller (pushObservers) gates the push on the S4 COMMIT outcome —
//              an aborted turn calls nothing, so the abort-inverse noise is never
//              projected; AND
//         (ii) editorStore's Gate #4 abort-rewind (`editorStore.ts` `history/
//              abort-batch`: rewinds `audit.nextSequence` to the begin-batch base
//              `pendingBatchBaseAuditSequence`, captured at ~3443, restored at
//              ~3623-3633) makes `post == pre` after an abort, so even if the caller
//              DID project the range it would be EMPTY (the abort-leak guard test
//              `state/__tests__/auditTelemetryAbortLeak.test.ts` pins this rewind).
//       Together: the staged range is never pushed on abort, and the live ring is
//       left clean (INV-AUDIT-TX).
//
// LEAF module: imports NOTHING. No socket, no editorStore, no contracts, no
// node-companion.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The SYNTHETIC bridge actor stamped on the audit ring (and the push frame) for a
 * bridge turn. A `.email()`-valid address (Zod 4 `.email()` ACCEPTS the reserved
 * `.invalid` TLD — RFC 6761 — while rejecting `@localhost`/`@127.0.0.1`), so the
 * `auditEventSchema.actorEmail` validator passes. The `.invalid` TLD GUARANTEES it
 * can never collide with a real user mailbox (INV-SOURCE distinct-actor).
 */
export const BRIDGE_AUDIT_ACTOR_EMAIL = "agent-bridge@local.invalid" as const;

/**
 * The audit-ring `channel` value a bridge turn stamps (the ABB-S2 typed
 * `auditEventSchema.channel` enum member). DISTINCT from the closed
 * `commandSourceSchema` (`source` stays `'system'`, INV-6) — the channel answers
 * "who drove it" so the History panel can attribute a `source:'system'` mutation
 * to the bridge actor rather than collapsing every system command into one bucket.
 */
export const BRIDGE_AUDIT_CHANNEL = "agent-bridge" as const;

// ── (1) the ambient bridge-turn marker ───────────────────────────────────────

/**
 * Non-reentrant depth counter for the ambient marker. A depth counter (not a bare
 * boolean) so a defensive nested begin/end pair is balanced; the single-in-flight
 * latch (ABB-S4 INV-INFLIGHT) already forbids a TRUE concurrent nest, so in
 * practice the depth is 0 or 1.
 */
let bridgeTurnDepth = 0;

/**
 * Enter the ambient "current bridge turn" context. S4 calls this IMMEDIATELY
 * before the synchronous `runContour` call (its `beginBridgeTurn?` DI). MUST be
 * paired with exactly one {@link endBridgeTurn} in a `finally` so the marker clears
 * on EVERY exit (success / throw / abort / supersede).
 */
export function beginBridgeTurn(): void {
  bridgeTurnDepth += 1;
}

/**
 * Leave the ambient bridge-turn context (S4's `endBridgeTurn?` DI, called in a
 * `finally`). Idempotent at zero (a stray end never drives the depth negative — a
 * negative depth would silently keep a future `isBridgeTurnActive()` true once it
 * returns to 0 only by coincidence). Clamped at 0.
 */
export function endBridgeTurn(): void {
  if (bridgeTurnDepth > 0) {
    bridgeTurnDepth -= 1;
  }
}

/**
 * Whether a bridge turn is ambiently active RIGHT NOW. Read by
 * `state/reducerHelpers.ts appendAuditEvent` to decide whether to override the
 * ring entry's `actorEmail` + stamp `channel`. A pure synchronous read — the
 * marker is set/cleared on the SAME JS tick as the synchronous contour, so a ring
 * append that happens during the contour dispatch sees it true, and any append
 * OUTSIDE a bridge turn sees it false.
 */
export function isBridgeTurnActive(): boolean {
  return bridgeTurnDepth > 0;
}

/**
 * TEST-ONLY: force-clear the ambient marker (a test that exercises a throwing path
 * without the S4 `finally` may leave it set). Production code never calls this — it
 * always pairs begin/end in a `finally`. Exported so unit tests reset module state
 * between cases.
 */
export function __resetBridgeTurnForTest(): void {
  bridgeTurnDepth = 0;
}

// ── (2) the staged-audit projection ──────────────────────────────────────────

/**
 * The minimal shape of one audit-ring entry the staging projection reads. A
 * STRUCTURAL subset of the contract's `AuditEvent` (this leaf imports no
 * contracts) — only `sequence` is load-bearing for the range filter; the rest is
 * carried opaquely into the push payload.
 */
export interface StagedAuditEntry {
  readonly sequence: number;
  readonly [k: string]: unknown;
}

/**
 * Project the audit-ring entries this turn appended — those whose `sequence` is in
 * the HALF-OPEN range `[preHighWater, postHighWater)`. `getAuditHighWater()`
 * returns `state.audit.nextSequence` (the EXCLUSIVE next-seq), so `preHighWater` is
 * the seq of this turn's FIRST event (inclusive) and `postHighWater` is the
 * next-after-this-turn seq (exclusive): the turn's own events are `[pre, post)`.
 *
 * This is the SINGLE source of truth for BOTH the ABB-S5 audit PUSH payload AND the
 * ABB-S4 rpc-result `auditRefs`: the command.run handler calls this ONCE
 * SYNCHRONOUSLY at commit (BEFORE the batchHash await, P2-2), maps `.sequence` over
 * the result for `auditRefs`, and surfaces the SAME projected array as `auditEvents`
 * for the push — so the two describe the SAME set BY CONSTRUCTION (P2-1), and both
 * are immune to a ring eviction during the await. The contract is the
 * SURVIVING ring entries in that range, NOT the full numeric span: the ring is
 * hard-capped at 24 entries (`reducerHelpers.appendAuditEvent` slices `events` to
 * 24), so a turn that appends >24 events evicts its own oldest rows; a cap-evicted
 * event is in NEITHER the auditRefs NOR the push (never an auditRef id with no
 * pushed body). The ring stores entries NEWEST-FIRST (the shape
 * `appendAuditEvent` prepends), so this FILTERS the ring by sequence rather than
 * slicing by index — robust to the cap (an evicted entry is simply absent, never a
 * stale older entry). Returns the matched entries in ASCENDING sequence order (the
 * chronological turn order the agent's journal expects).
 *
 * On an ABORTED turn the CALLER passes the SAME `pre`/`post` it would on success but
 * MUST DISCARD the result (not push it) — see INV-AUDIT-TX; this pure projector does
 * not itself know commit-vs-abort, so the caller (pushObservers) gates on the S4
 * outcome.
 */
export function projectStagedAuditEvents<T extends StagedAuditEntry>(
  ringNewestFirst: readonly T[],
  preHighWater: number,
  postHighWater: number,
): T[] {
  if (!Number.isFinite(preHighWater) || !Number.isFinite(postHighWater)) {
    return [];
  }
  if (postHighWater <= preHighWater) {
    return []; // an empty / no-op / aborted turn appended nothing in [pre, post).
  }
  const matched = ringNewestFirst.filter(
    (entry) =>
      typeof entry.sequence === "number" &&
      entry.sequence >= preHighWater &&
      entry.sequence < postHighWater,
  );
  // Ascending by sequence (the ring is newest-first; the journal wants chronological).
  return matched.slice().sort((a, b) => a.sequence - b.sequence);
}
